import { Add01Icon } from "@hugeicons/core-free-icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Tag } from "@/components/common/badges";
import { Icon } from "@/components/common/icon";
import { Panel } from "@/components/common/page";
import { Button } from "@/components/ui/button";
import { q } from "@/data/queries";
import { formatDateTime, initials, relativeTime } from "@/lib/format";

const ROLE_NOTE: Record<string, string> = {
  owner: "Everything, including billing and deleting the organisation",
  admin: "Everything except billing",
  developer: "Keys, webhooks and accounts; not members or billing",
  viewer: "Read-only, including the audit log",
};

export const Route = createFileRoute("/p/$project/settings/members")({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(q.members()),
      context.queryClient.ensureQueryData(q.invitations()),
    ]),
  component: MembersPage,
});

function MembersPage() {
  const { data: members } = useSuspenseQuery(q.members());
  const { data: invitations } = useSuspenseQuery(q.invitations());

  return (
    <>
      <Panel
        title={`Members (${members.length})`}
        actions={
          <Button size="sm" disabled>
            <Icon icon={Add01Icon} className="size-3.5" />
            Invite
          </Button>
        }
        bodyClassName="divide-y"
      >
        {members.map((member) => (
          <div key={member.id} className="flex items-center gap-3 px-4 py-2.5">
            <span
              aria-hidden
              className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-soft text-[10px] font-semibold text-brand"
            >
              {initials(member.name)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium">
                {member.name}
              </span>
              <span className="block truncate text-[11.5px] text-muted-foreground">
                {member.email}
              </span>
            </span>
            <Tag
              mono
              title={ROLE_NOTE[member.role]}
              className={
                member.role === "owner"
                  ? "border-brand/30 bg-brand-soft/60 text-brand"
                  : "border-border text-muted-foreground"
              }
            >
              {member.role}
            </Tag>
            <span className="hidden w-28 shrink-0 text-right text-[11px] text-ink-faint sm:block">
              {relativeTime(member.lastSeenAt)}
            </span>
          </div>
        ))}
      </Panel>

      {invitations.length > 0 ? (
        <Panel title="Pending invitations" bodyClassName="divide-y">
          {invitations.map((invitation) => (
            <div
              key={invitation.id}
              className="flex flex-wrap items-center gap-3 px-4 py-2.5"
            >
              <span className="min-w-0 flex-1 truncate text-[13px]">
                {invitation.email}
              </span>
              <Tag mono>{invitation.role}</Tag>
              <span className="text-[11px] text-ink-faint">
                expires {formatDateTime(invitation.expiresAt)}
              </span>
              <Button variant="ghost" size="sm" disabled>
                Revoke
              </Button>
            </div>
          ))}
        </Panel>
      ) : null}
    </>
  );
}
